import { noop, uniqueId } from 'lodash-es'
import { useDispatch, useSelector } from 'react-redux'
import { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import cc from 'classcat'

import { setThemeNotification } from '../../store/actions'
import { track } from '../../utils/tracking'
import { useTranslate } from '../../utils/translate'

export default function AddToWishlistLinkRaw({
  children,
  className,
  productId,
  quantity = 1,
  lineItemId,
  callAfterItemAddedToWishlist = noop,
  title,
  customizedText = null,
  isCustomizableProduct = false,
}) {
  const [isWishlistBusy, setIsWishlistBusy] = useState(false)

  const locale = useSelector((state) => state.getIn(['shop', 'locale']))
  const dispatch = useDispatch()

  const t = useTranslate('shop')

  const isCustomizationMissing = isCustomizableProduct && !customizedText

  useEffect(() => {
    const handleWishlistSuccess = (event) => {
      const notification = {
        id: uniqueId(`${productId}-success-`),
        message: (
          <span>
            {t('notifications.productAddedToWishlist')}{' '}
            <a className="add-to-cart-notice-message-link" href={event.detail.wishlist} key={productId}>
              {t('views.storefrontView.goToWishlistLink.label')}
            </a>
          </span>
        ),
        type: 'success',
      }
      callAfterItemAddedToWishlist()
      // Reset the notification message to make sure that it is announced to screen readers
      // after a short delay, which is necessary for live regions to work correctly.
      // See Notification.tsx for more details.
      dispatch(setThemeNotification({ ...notification, message: '' }))
      setTimeout(() => {
        dispatch(setThemeNotification(notification))
        setIsWishlistBusy(false)
      }, 100)
    }
    const handleWishlistNoUser = (event) => {
      window.addEventListener('pagehide', () => setIsWishlistBusy(false))
      window.location.assign(event.detail.redirect)
    }

    window.eComEventTarget.addEventListener(`wishlist:success-${lineItemId ?? productId}`, handleWishlistSuccess)
    window.eComEventTarget.addEventListener(`wishlist:nouser-${lineItemId ?? productId}`, handleWishlistNoUser)

    return () => {
      window.eComEventTarget.removeEventListener(`wishlist:success-${lineItemId ?? productId}`, handleWishlistSuccess)
      window.eComEventTarget.removeEventListener(`wishlist:nouser-${lineItemId ?? productId}`, handleWishlistNoUser)
    }
  }, [dispatch, productId, lineItemId, t, callAfterItemAddedToWishlist])

  const addToWishlist = () => {
    setIsWishlistBusy(true)

    track('wishlist:add', {
      locale,
      product: {
        productId,
        quantity,
        customizedText: isCustomizableProduct ? customizedText : null,
      },
      lineItemId,
    })
  }

  return (
    <button
      className={cc([className, { pending: isWishlistBusy }])}
      disabled={isWishlistBusy || isCustomizationMissing}
      onClick={addToWishlist}
      type="button"
      title={title}
      aria-label={title}
    >
      {children}
    </button>
  )
}

AddToWishlistLinkRaw.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  isWishlistBusy: PropTypes.bool,
  productId: PropTypes.string.isRequired,
  quantity: PropTypes.number,
  lineItemId: PropTypes.string,
  callAfterItemAddedToWishlist: PropTypes.func,
  title: PropTypes.string,
  customizedText: PropTypes.string,
  isCustomizableProduct: PropTypes.bool,
  isInWishlist: PropTypes.bool,
}
